import { Avatar, Box, Typography } from '@mui/material'
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid-pro'
import { stringAvatar } from 'components/DataGrid/gridOperators'
import { FC, memo } from 'react'
import { DensityStyleTypes, OrganizationTableItem } from 'types'

interface MtbOrgCellProps {
  params: GridRenderCellParams
  densityStyles: DensityStyleTypes
  handleRowClick: (row: OrganizationTableItem) => void
  toggleExpanded: (id: GridRowId) => void
}

// Custom comparison for memo to properly detect changes
const arePropsEqual = (prevProps: MtbOrgCellProps, nextProps: MtbOrgCellProps): boolean => {
  // Always re-render if row identity changes
  if (prevProps.params.id !== nextProps.params.id) return false
  // Check if row data changed
  if (prevProps.params.row !== nextProps.params.row) return false
  // Check if value changed
  if (prevProps.params.value !== nextProps.params.value) return false
  // Check density styles
  if (prevProps.densityStyles !== nextProps.densityStyles) return false
  // Check handlers (reference equality is fine here since they're memoized)
  if (prevProps.handleRowClick !== nextProps.handleRowClick) return false
  if (prevProps.toggleExpanded !== nextProps.toggleExpanded) return false
  return true
}

const MtbOrganizationCell: FC<MtbOrgCellProps> = memo(({ params, densityStyles, handleRowClick, toggleExpanded }) => {
  const { rowNode, row: _row, value } = params
  const row = params.row
  const { PK, isParent } = row
  const _org_uuid = PK?.split('ORG_')?.[1]
  const isChild = !isParent
  const depth = rowNode?.depth ?? 0
  const indent = depth * 20

  // only parent rows get an expand button
  const isGroup = isParent
  // cast away TS so we can see the internal children array
  const internalNode = rowNode as any
  const hasChildren = params?.row?.hasChildRows ?? (internalNode.children?.length ?? 0) > 0
  const isExpanded = Boolean(internalNode.childrenExpanded)

  const organization = params?.row ?? null
  const _orgName = organization?.name ?? ''
  //@ts-ignore
  if (params?.rowNode?.isAutoGenerated) return
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        pl: isChild ? `${indent}px` : densityStyles.padding,
        pt: densityStyles.padding,
        pb: densityStyles.padding,
        pr: densityStyles.padding,
        gap: densityStyles.gap,
        width: '100%',
        cursor: 'pointer',
      }}
      onClick={() => handleRowClick(row)}
    >
      <>
        {/* avatar only on parent rows */}
        {isParent &&
          (organization?.photoUrl ? (
            <Avatar
              src={organization.photoUrl}
              sx={{
                width: densityStyles.avatarSize,
                height: densityStyles.avatarSize,
                objectFit: 'cover',
                objectPosition: 'center',
                ml: 1,
              }}
              alt={value}
            />
          ) : (
            <Avatar
              {...stringAvatar(value, densityStyles)}
              sx={{
                backgroundColor: 'rgb(114, 229, 218)',
                width: densityStyles.avatarSize,
                height: densityStyles.avatarSize,
                ml: 1,
              }}
              alt={value}
            />
          ))}

        {/* finally, the row label */}
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            fontSize: densityStyles.fontSize,
            fontWeight: isParent ? 600 : 400,
            ml: isParent ? 1 : 0,
            color: row?.isOrgTask ? 'text.highlight' : 'text.primary',
          }}
        >
          {value}
        </Typography>
      </>
    </Box>
  )
}, arePropsEqual)

export default MtbOrganizationCell
